import { datadogRum } from '@datadog/browser-rum';
import semver from 'semver';
import { VERSION_FILE_URL } from 'utilities/consts.js';
import { formatUrl } from 'utilities/fetch/url.js';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';
import { getWindowLocation } from 'utilities/window.js';
import { FetchError, RequestError } from './api-gateway.js';

export const MANUAL_REDIRECT = 'opaqueredirect';
export const MANUAL_REDIRECT_RESPONSE = 'REDIRECTED';

/** Make 'public/version.json' fetch request */
export const makeVersionRequest = async <T>(): Promise<T | typeof MANUAL_REDIRECT_RESPONSE | undefined> => {
  const finalUrl = formatUrl({ path: VERSION_FILE_URL, apiPrefix: false });
  try {
    const response = await fetch(finalUrl, {
      cache: 'no-store',
      redirect: 'manual',
    });
    if (response.type === MANUAL_REDIRECT) {
      return MANUAL_REDIRECT_RESPONSE;
    }
    if (response.ok) {
      const responseJson = await response.json();
      return responseJson;
    } else {
      // Server Response Error
      throw new RequestError(response.status, FetchError.NETWORK_ERROR, `${response.status}: ${response.statusText}`);
    }
  } catch (e) {
    console.log('Version Request Error', e, finalUrl);
  }
};

/** Checks version against that of server */
export const manageSiteVersion = async (appVersion: string | undefined, log?: boolean) => {
  const checkVersion = await makeVersionRequest<Record<'Version' | 'BuildDate' | 'CommitHash', string>>();
  if (log) {
    datadogRum.setGlobalContext({
      appVersion: appVersion,
      serverVersion: checkVersion === MANUAL_REDIRECT_RESPONSE ? MANUAL_REDIRECT_RESPONSE : checkVersion?.Version,
    });
    messageLog.trace(
      `VERSION CHECKING: ${appVersion}, ${checkVersion === MANUAL_REDIRECT_RESPONSE ? MANUAL_REDIRECT_RESPONSE : checkVersion?.Version}`,
    );
  }

  if (checkVersion === MANUAL_REDIRECT_RESPONSE) {
    /** Request for public/version.json is redirecting so probably pointing to www2.
     * Reload */
    messageLog.notice(`VERSION REDIRECTING: Reloading...`);
    getWindowLocation().reload();
  } else {
    const fetchedVersion = checkVersion?.Version;
    const semverUpdated = isSemverUpdated(fetchedVersion, appVersion);
    if (semverUpdated) {
      getWindowLocation().reload();
    }
  }
};

const isSemverUpdated = (fetchedVersion?: string, appVersion?: string): boolean => {
  if (fetchedVersion && semver.valid(fetchedVersion) && appVersion && semver.valid(appVersion)) {
    if (fetchedVersion !== appVersion) {
      messageLog.notice(`VERSION UPDATE: Reloading...`, ['Version Details', { appVersion, fetchedVersion }]);
      return true;
    }
  }
  return false;
};
